import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useDispatch, useSelector } from 'react-redux';

import {
  selectActiveFundingSourceOperation,
  selectClient,
  selectSession,
  selectSessionAdvisor,
} from '@client/model/selectors';
import { putMultipleFundingSourceForClientByClientIdRequest } from '@client/model';
import { SessionType } from '@shared';

import {
  ASSET_TYPES,
  ASSET_TYPE_TO_ACCOUNT_TYPE,
  type AssetTypeId,
} from '@/features/guided-financial-planning/config';

// ─────────────────────────────────────────────────────────────
// SCHEMA
// ─────────────────────────────────────────────────────────────

const selfFundingFormSchema = z.object({
  existingAssetsValue: z.number({ required_error: 'Required' }).min(0, 'Must be 0 or greater'),
  yearlyContribution: z.number().nullable(),
  annualRateOfReturn: z.number({ required_error: 'Required' }),
  gainsTaxRate: z.number({ required_error: 'Required' }),
});

export type SelfFundingFormValues = z.infer<typeof selfFundingFormSchema>;

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

function getAssetLabel(assetTypeId: AssetTypeId): string {
  return ASSET_TYPES.find(asset => asset.id === assetTypeId)?.label ?? assetTypeId;
}

// ─────────────────────────────────────────────────────────────
// HOOK
// ─────────────────────────────────────────────────────────────

export function useSelfFundingForm(
  assetTypeId: AssetTypeId,
  fundingSourceId: string | null,
  existingSource?: SelfFundingSource,
) {
  const client = useSelector(selectClient);
  const { sessionType } = useSelector(selectSession);
  const { advisorId } = useSelector(selectSessionAdvisor);
  const activeFundingSourceOperation = useSelector(selectActiveFundingSourceOperation);

  const clientId = client.clientId;
  const inference = client.appliedInferenceSet;
  const ltcAtYear =
    inference?.ltcAtYear ??
    (inference?.yearsTillLtc != null
      ? new Date().getFullYear() + Math.max(0, Math.round(inference.yearsTillLtc))
      : new Date().getFullYear() + 30);

  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;

  const dispatch = useDispatch();

  // Track if this specific funding source is being saved
  const isSaving =
    activeFundingSourceOperation !== null &&
    activeFundingSourceOperation.fundingSourceId === fundingSourceId;

  const form = useForm<SelfFundingFormValues>({
    resolver: zodResolver(selfFundingFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      existingAssetsValue: existingSource?.existingAssetsValue ?? undefined,
      yearlyContribution: existingSource?.monthlyContributionAmount
        ? existingSource.monthlyContributionAmount * 12
        : null,
      annualRateOfReturn: existingSource?.annualRateOfReturn ?? undefined,
      gainsTaxRate: existingSource?.gainsTaxRate ?? undefined,
    },
  });

  // Reset form when fundingSourceId or existingSource changes
  useEffect(() => {
    form.reset({
      existingAssetsValue: existingSource?.existingAssetsValue ?? undefined,
      yearlyContribution: existingSource?.monthlyContributionAmount
        ? existingSource.monthlyContributionAmount * 12
        : null,
      annualRateOfReturn: existingSource?.annualRateOfReturn ?? undefined,
      gainsTaxRate: existingSource?.gainsTaxRate ?? undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fundingSourceId, existingSource]);

  const buildSourceData = useCallback(
    (data: SelfFundingFormValues): SelfFundingSource => {
      const accountType = ASSET_TYPE_TO_ACCOUNT_TYPE[assetTypeId];
      const accountName = getAssetLabel(assetTypeId);

      return {
        hasSelfFunding: true,
        selfFundingName: existingSource?.selfFundingName ?? accountName,
        accountType,
        existingAssetsValue: data.existingAssetsValue,
        annualRateOfReturn: data.annualRateOfReturn,
        contributionStartYear: new Date().getFullYear(),
        contributionEndYear: ltcAtYear,
        monthlyContributionAmount: (data.yearlyContribution ?? 0) / 12,
        gainsTaxRate: data.gainsTaxRate,
        monthlyIncome: null,
        contributionMonthCount: 0,
        ...(isAdvisorSession && { addedByAdvisorId: advisorId }),
      } as SelfFundingSource;
    },
    [assetTypeId, existingSource?.selfFundingName, ltcAtYear, isAdvisorSession, advisorId],
  );

  const onSubmit = useCallback(
    (data: SelfFundingFormValues) => {
      const updatedSelfFunding = buildSourceData(data);

      dispatch(
        putMultipleFundingSourceForClientByClientIdRequest({
          clientId,
          fundingSourceType: 'selfFunding',
          fundingSourceData: updatedSelfFunding,
          fundingSourceId: fundingSourceId ?? null,
        }),
      );
    },
    [buildSourceData, clientId, fundingSourceId, dispatch],
  );

  return {
    form,
    onSubmit,
    buildSourceData,
    isSaving,
    clientId,
  };
}
